import { useRef } from "react";
import { motion, useInView } from "framer-motion";

// ─── Datos de distancias ───────────────────────────────────────────────────
const destinos = [
  { name: "Alta Gracia", dist: "aquí", propio: true },
  { name: "Córdoba Capital", dist: "36 km" },
  { name: "Carlos Paz", dist: "20 km" },
  { name: "V. Gral. Belgrano", dist: "40 km" },
  { name: "La Cumbrecita", dist: "60 km" },
];

// ─── URLs de contacto ─────────────────────────────────────────────────────
const WHATSAPP_URL = "https://wa.me/5493513484082";
const INSTAGRAM_URL = "https://www.instagram.com/touring_cab?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==";
const GMAPS_URL = "https://maps.app.goo.gl/h4zXhiCgmEBPNTuq7";
const GMAPS_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2649.340443700518!2d-64.4395927253847!3d-31.63885700703961!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x942d50aa31bfe285%3A0x3a8ae0e30c4cc691!2sCaba%C3%B1as%20Touring%20Club!5e1!3m2!1ses-419!2sar!4v1778545770531!5m2!1ses-419!2sar";
// ─── Variantes de animación ───────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay, ease: "easeOut" } },
});

const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

// ─── Componente principal ─────────────────────────────────────────────────
export default function EncontranosFusionado() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });

  return (
    <section
      ref={ref}
      className="snap-start h-screen flex flex-col"
      style={{ background: "#2a1a0e" }}
    >
      {/* ── Contenido principal ── */}
      <div className="flex-1 flex items-center px-6 md:px-16 py-8 md:py-16 overflow-hidden">
        <div className="w-full max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-6 md:gap-16">

          {/* Columna izquierda — Mapa circular */}
          <motion.div
            variants={fadeLeft}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="flex flex-shrink-0 flex-col items-center gap-3 md:gap-6"
          >
            {/* Círculo del mapa */}
            <div
              className="relative rounded-full overflow-hidden"
              style={{
                width: "clamp(220px, 65vw, 320px)",
                height: "clamp(220px, 65vw, 320px)",
                border: "3px solid #3d2510",
                boxShadow: "0 0 0 8px rgba(61,37,16,0.3), 0 0 60px rgba(242,117,7,0.08)",
              }}
            >
              {/* Viñeta interna para que se funda con el borde */}
              
              <iframe
                title="Ubicación Cabañas Touring Club"
                src={GMAPS_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0, filter: "sepia(30%) saturate(0.8) brightness(0.85)" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              {/* Viñeta interna para que se funda con el borde */}
              <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  boxShadow: "inset 0 0 40px rgba(42,26,14,0.7)",
                }}
              />
            </div>

            {/* CTA bajo el mapa */}
            <motion.a
              href={GMAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeUp(0.4)}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="flex items-center gap-2 px-6 py-2.5 text-xs tracking-widest uppercase transition-opacity hover:opacity-80"
              style={{
                color: "#e8d5b0",
                border: "1px solid #F27507",
                borderRadius: "2px",
                letterSpacing: "0.12em",
              }}
            >
              Ver en Google Maps
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
              </svg>
            </motion.a>
          </motion.div>

          {/* Columna central — Texto */}
          <motion.div
            variants={fadeUp(0.1)}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="flex-1 min-w-0"
          >
            <p
              className="text-xs tracking-widest uppercase mb-3"
              style={{ color: "#F27507", letterSpacing: "0.2em" }}
            >
              Ubicación
            </p>
            <h2
              className="titulo-playfair italic font-bold mb-3 md:mb-5 leading-snug"
              style={{ color: "#e8d5b0", fontSize: "clamp(1.4rem, 3vw, 2.4rem)" }}
            >
              Un punto estratégico<br className="hidden md:block" /> para disfrutar
            </h2>
            <p
              className="hidden md:block text-sm leading-relaxed mb-8 max-w-md"
              style={{ color: "#9a7a50" }}
            >
              Estamos ubicados en un punto estratégico donde podés disfrutar tu
              estadía en un lugar tranquilo y a su vez tener puntos turísticos
              cerca: Carlos Paz, Villa General Belgrano, La Cumbrecita, y más.
            </p>

            {/* Links de contacto */}
            <div className="flex flex-wrap gap-3 mb-2 md:mb-0">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 text-xs transition-opacity hover:opacity-80"
                style={{
                  color: "#c9aa80",
                  border: "1px solid #3d2510",
                  borderRadius: "20px",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.121 1.533 5.851L0 24l6.335-1.508A11.956 11.956 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.37l-.36-.214-3.72.885.939-3.618-.235-.372A9.818 9.818 0 1112 21.818z"/>
                </svg>
                WhatsApp
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 text-xs transition-opacity hover:opacity-80"
                style={{
                  color: "#c9aa80",
                  border: "1px solid #3d2510",
                  borderRadius: "20px",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
                Instagram
              </a>
            </div>
          </motion.div>

          {/* Columna derecha — Distancias */}
          <motion.div
            variants={fadeRight}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="hidden md:block flex-shrink-0 w-44"
          >
            <p
              className="text-xs tracking-widest uppercase mb-4"
              style={{ color: "#5a3e28", letterSpacing: "0.2em" }}
            >
              Distancias
            </p>
            <div className="flex flex-col">
              {destinos.map((d, i) => (
                <div
                  key={d.name}
                  className="flex items-center gap-3 py-3"
                  style={{
                    borderBottom: i < destinos.length - 1 ? "1px solid #3d2510" : "none",
                  }}
                >
                  <div
                    className="flex-shrink-0 w-2 h-2 rounded-full"
                    style={{
                      background: d.propio ? "#F27507" : "#3d2510",
                      boxShadow: d.propio ? "0 0 6px rgba(242,117,7,0.5)" : "none",
                    }}
                  />
                  <span
                    className="text-xs flex-1"
                    style={{ color: d.propio ? "#e8d5b0" : "#9a7a50" }}
                  >
                    {d.name}
                  </span>
                  <span
                    className="text-xs"
                    style={{ color: d.propio ? "#F27507" : "#5a3e28" }}
                  >
                    {d.dist}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Separador ── */}
      <div style={{ borderTop: "1px solid #3d2510", margin: "0 24px" }} />

      {/* ── Footer ── */}
      <footer className="px-6 md:px-16 py-5 md:py-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">

          {/* Logo / nombre */}
          <div>
            <p
              className="titulo-playfair italic font-bold"
              style={{ color: "#e8d5b0", fontSize: "1.1rem" }}
            >
              Cabañas Touring Club
            </p>
            <p className="text-xs mt-1" style={{ color: "#5a3e28", letterSpacing: "0.15em" }}>
              Alta Gracia · Córdoba · Argentina
            </p>
          </div>

          {/* Navegación */}
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {["Inicio", "Complejo", "Cabañas", "Fotos", "Reseñas"].map((item) => (
              <a
                key={item}
                href={`#${item.replace(/\s/g, "-")}`}
                className="text-xs transition-colors hover:text-amber-400"
                style={{ color: "#9a7a50" }}
              >
                {item}
              </a>
            ))}
          </nav>

          {/* Redes icono */}
          <div className="flex gap-3">
            {[
              { href: WHATSAPP_URL, label: "WhatsApp", icon: <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>, fill: true },
              { href: INSTAGRAM_URL, label: "Instagram", icon: <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>, fill: true },
            ].map(({ href, label, icon, fill }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex items-center justify-center transition-colors hover:border-amber-600"
                style={{
                  width: 32,
                  height: 32,
                  border: "1px solid #3d2510",
                  borderRadius: "50%",
                  color: "#9a7a50",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill={fill ? "currentColor" : "none"} stroke={fill ? "none" : "currentColor"} strokeWidth="2" aria-hidden="true">
                  {icon}
                </svg>
              </a>
            ))}
          </div>
        </div>

        {/* Copyright bar */}
        <div
          className="max-w-6xl mx-auto mt-6 pt-5 flex flex-col md:flex-row items-center justify-between gap-2 text-xs"
          style={{ borderTop: "1px solid #3d2510", color: "#5a3e28" }}
        >
          <span>© {new Date().getFullYear()} Cabañas Touring Club. Todos los derechos reservados.</span>
          <span>
            Diseño y desarrollo por{" "}
            <span style={{ color: "#9a7a50" }}>Luna Code Studio</span>
          </span>
        </div>
      </footer>
    </section>
  );
}